import React, { PropsWithChildren, useCallback, useMemo } from "react";

import { StyledMultiAvatar, StyledMultiAvatarInner } from "./Styles";

export interface MultiAvatarProps {
  /** Path url. */
  url: string[];
  /** Alt text. */
  alt?: string[];
  /** Image size. */
  size?: number;
  /** Custom class. */
  className?: string;
}

/**
 * @component MultiAvatar
 * @description
 * The MultiAvatar component is used to represent user, and displays the profile picture, initials or fallback icon.
 *
 * @prop {url} - The image urls of the MultiAvatar.
 * @prop {size} - controls the size of an MultiAvatar (width and height) in pixels.
 * @prop {alt} - alt attributes of the imgs.
 *
 * @example
 * <MultiAvatar
 *  url={["https://faces-img.xcdn.link/image-lorem-face-954.jpg", "https://faces-img.xcdn.link/image-lorem-face-953.jpg"]}
 *  alt={["Profile picture of John", "Profile picture of Sarah"]} />
 *
 * @example
 * <MultiAvatar
 *  url={["https://faces-img.xcdn.link/image-lorem-face-954.jpg", "https://faces-img.xcdn.link/image-lorem-face-953.jpg"]}
 *  alt={["Profile picture of John", "Profile picture of Sarah"]}
 * >
 *  <Badge />
 * </MultiAvatar>
 *
 * @see
 * https://system.activecollab.com/?path=/story/components-button-indicators-avatar--avatar
 * @see
 * https://design.activecollab.com/docs/components/avatar
 */
export const MultiAvatar = ({
  url,
  alt,
  size = 24,
  className,
  children,
}: PropsWithChildren<MultiAvatarProps>) => {
  const setSize = useCallback(
    (i: number): object => {
      switch (url.length) {
        case 1:
          return { width: `${size}px`, height: `${size}px` };
        case 2:
          return { width: `${size / 2}px`, height: `${size}px` };
        case 3:
          if (i === 0) {
            return { width: `${size / 2}px`, height: `${size}px` };
          } else {
            return { width: `${size / 2}px`, height: `${size / 2}px` };
          }
        default:
          return { width: `${size / 2}px`, height: `${size / 2}px` };
      }
    },
    [size, url.length]
  );

  const slicedUrl = useMemo(() => {
    if (url?.length > 4) {
      return url.slice(0, 4);
    }

    return url;
  }, [url]);

  return (
    <span className="tw-relative tw-inline-flex">
      <StyledMultiAvatar
        style={{ width: `${size}px`, height: `${size}px` }}
        className={className}
        $urlCount={url.length}
      >
        {slicedUrl.map((v, i) => (
          <StyledMultiAvatarInner
            style={setSize(i)}
            className="c-multi-avatar__wrapper"
            key={`avatar_${i}`}
          >
            <img
              src={v}
              alt={alt && alt[i] ? alt[i] : `avatar_${i}`}
              width={size}
              height={size}
            />
          </StyledMultiAvatarInner>
        ))}
      </StyledMultiAvatar>
      {children}
    </span>
  );
};

MultiAvatar.displayName = "MultiAvatar";
